import React, { useState, useEffect, useRef, useCallback } from 'react'
import { Mic, MicOff } from 'lucide-react'
import { VoiceInputProps } from '../types'

const VoiceInput: React.FC<VoiceInputProps> = ({ 
  onTranscript, 
  isListening, 
  setIsListening, 
  disabled = false 
}) => {
  const [isSupported, setIsSupported] = useState(false)
  const [currentTranscript, setCurrentTranscript] = useState('')
  const recognitionRef = useRef<SpeechRecognition | null>(null)
  const finalTranscriptRef = useRef('')

  // Memoize the onTranscript callback to prevent stale closures
  const handleTranscript = useCallback((transcript: string) => {
    if (transcript.trim()) {
      onTranscript(transcript.trim())
    }
  }, [onTranscript])

  useEffect(() => {
    // Check if Web Speech API is supported
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      setIsSupported(true)
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition
      
      const recognition = new SpeechRecognition()
      recognitionRef.current = recognition
      
      recognition.continuous = false
      recognition.interimResults = true
      recognition.lang = 'en-US'

      recognition.onresult = (event) => {
        let finalTranscript = ''
        let interimTranscript = ''

        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript
          if (event.results[i].isFinal) {
            finalTranscript += transcript
          } else {
            interimTranscript += transcript
          }
        }

        // Store final transcript separately
        finalTranscriptRef.current = finalTranscript
        const fullTranscript = finalTranscript + interimTranscript
        setCurrentTranscript(fullTranscript)
      }

      recognition.onend = () => {
        setIsListening(false)
        // Only send if we have a final transcript (not just interim)
        if (finalTranscriptRef.current.trim()) {
          handleTranscript(finalTranscriptRef.current)
        }
        // Reset all transcripts
        setCurrentTranscript('')
        finalTranscriptRef.current = ''
      }

      recognition.onerror = (event) => {
        console.error('Speech recognition error:', event.error)
        setIsListening(false)
        setCurrentTranscript('')
        finalTranscriptRef.current = ''
      }

      return () => {
        if (recognition) {
          recognition.stop()
        }
      }
    }
  }, [handleTranscript])

  const startListening = () => {
    if (!isSupported || disabled) return
    
    // Reset transcripts before starting
    setCurrentTranscript('')
    finalTranscriptRef.current = ''
    
    // Stop any existing recognition
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop()
      } catch (e) {
        // Ignore errors from stopping
      }
    }
    
    // Start new recognition
    recognitionRef.current?.start()
    setIsListening(true)
  }

  const stopListening = () => {
    if (recognitionRef.current && isListening) {
      recognitionRef.current.stop()
    }
  }

  if (!isSupported) {
    return (
      <div className="text-center py-8">
        <MicOff className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-600 mb-2">Voice features not supported</p>
        <p className="text-sm text-gray-500">
          Please use a modern browser with Web Speech API support
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-3">
      {/* Voice Button */}
      <div className="flex items-center justify-center">
        <button
          onMouseDown={startListening}
          onMouseUp={stopListening}
          onMouseLeave={stopListening}
          onTouchStart={startListening}
          onTouchEnd={stopListening}
          disabled={disabled}
          className={`
            flex items-center justify-center w-12 h-12 rounded-full transition-all duration-200
            ${isListening 
              ? 'bg-red-500 hover:bg-red-600 text-white animate-pulse shadow-lg' 
              : 'bg-primary-600 hover:bg-primary-700 text-white shadow-md'
            }
            ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
          `}
          title={isListening ? 'Release to send question' : 'Press and hold to ask a question'}
        >
          <Mic className="h-5 w-5" />
        </button>
      </div>

      {/* Status */}
      <div className="text-center">
        <p className="text-xs text-gray-600">
          {isListening ? (
            <span className="flex items-center justify-center space-x-2">
              <div className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse"></div>
              <span>Recording...</span>
            </span>
          ) : (
            <span>Press and hold to ask a question</span>
          )}
        </p>
      </div>

      {/* Live Transcript */}
      {currentTranscript && (
        <div className="mt-2">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-2 min-h-[32px] max-h-[60px] overflow-y-auto">
            <p className="text-xs text-gray-800">{currentTranscript}</p>
          </div>
        </div>
      )}

      {/* Quick Instructions */}
      <div className="text-xs text-gray-500 text-center">
        <p>💡 Ask about algorithms, complexity, or code explanations</p>
      </div>
    </div>
  )
}

export default VoiceInput 